import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
} from "react";
import {
  ConnectionStatus,
  useWebSocketClient,
} from "../../api/WebSocketClient";
import { useSessionId } from "../../api/ApiClient";
import { useContractParamsContext } from "../ContractParams";
import {
  ContractUpdateNotification,
  ContractUpdateNotificationContext,
} from "./ContractUpdates";

export default function ContractUpdateNotificationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { token } = useContractParamsContext();
  const [updates, setUpdates] = useState<ContractUpdateNotification[]>([]);
  const enqueue = useCallback((update: ContractUpdateNotification) => {
    setUpdates((x) => [...x, update]);
  }, []);
  const clear = useCallback(() => setUpdates([]), []);
  useEffect(() => clear(), [token, clear]);
  const { onMessage, onOpen, status, send } = useWebSocketClient();
  useEffect(
    () =>
      onMessage((event) => {
        const update = JSON.parse(event.data) as ContractUpdateNotification;

        if (!update.contract) return;

        const contractToken =
          "sellerToken" in update.contract
            ? update.contract.sellerToken
            : update.contract.buyerToken;

        if (contractToken !== token) return;

        enqueue(update);
      }),
    [onMessage, enqueue, token]
  );
  const sessionId = useSessionId();
  const subscribeData = useMemo(() => {
    if (!token) return null;

    return {
      token,
      sessionId,
    };
  }, [token, sessionId]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  useEffect(() => {
    if (status !== ConnectionStatus.Connected) setIsSubscribed(false);
  }, [status]);
  useEffect(() => {
    if (!subscribeData) return;

    const unsubscribe = onOpen(async () => {
      send({
        action: "Subscribe",
        data: subscribeData,
      });
      setIsSubscribed(true);
    });

    return unsubscribe;
  }, [onOpen, send, subscribeData]);

  return (
    <ContractUpdateNotificationContext.Provider
      value={{
        updates,
        clear,
        isSubscribed,
      }}
    >
      {children}
    </ContractUpdateNotificationContext.Provider>
  );
}
